import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MapPoolSelector from '../veto/MapPoolSelector';
import VetoSimulator from '../veto/VetoSimulator';
import RankBadge from '../elo/RankBadge';
import usePermissions from '../../hooks/usePermissions';
import './CreateMatch.css';

/**
 * Interactive demo page — simulates the full create-match flow locally.
 * No backend calls. Use /create-demo (gated by SHOW_CREATE_DEMO feature flag)
 * to test player picker → team shuffle → map pool → veto without touching
 * production matches.
 */

const FAKE_PLAYERS = Array.from({ length: 10 }, (_, i) => ({
  discord_id: `demo-${i + 1}`,
  username: `Demo Player ${i + 1}`,
  elo: { elo: 900 + i * 60, rank: ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'][Math.min(4, Math.floor(i / 2))] },
  avatar: null,
}));

const STEPS = ['players', 'teams', 'map_pool', 'veto', 'launching'];
// Step labels reuse existing keys where the term already exists; resolved via
// t() at render time (module-level constant can't call hooks).
const STEP_LABEL_KEYS = {
  players: 'nav.players',
  teams: 'createMatch.teams',
  map_pool: 'nav.maps',
  veto: 'createMatch.veto',
  launching: 'createMatchDemo.stepDone',
};

function shuffle(arr) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function splitTeams(players) {
  const mixed = shuffle(players);
  const half = Math.ceil(mixed.length / 2);
  return {
    team1: mixed.slice(0, half),
    team2: mixed.slice(half),
  };
}

/**
 * Pure-frontend simulation of /match create — picker → balanced shuffle →
 * map pool → veto (delegated to VetoSimulator) → mock launching screen.
 *
 * No API calls, no Redis, no real Match record. Gated by SHOW_CREATE_DEMO
 * feature flag and used to dry-run UI changes against the full create
 * flow without burning real match IDs.
 */
export default function CreateMatchDemo() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [matchType, setMatchType] = useState('BO1');
  const [teams, setTeams] = useState({ team1: [], team2: [] });
  const [selectedPool, setSelectedPool] = useState([]);
  const [vetoConfig, setVetoConfig] = useState({ match_type: 'BO1', veto_mode: 'team_vote', knife_round: true });
  const [resetKey, setResetKey] = useState(0);
  const { features } = usePermissions();

  const currentStep = STEPS[step];

  const togglePlayer = (player) => {
    setSelectedPlayers(prev => {
      const exists = prev.find(p => p.discord_id === player.discord_id);
      if (exists) return prev.filter(p => p.discord_id !== player.discord_id);
      if (prev.length >= 10) return prev;
      return [...prev, player];
    });
  };

  const pickAll = () => setSelectedPlayers(FAKE_PLAYERS);
  const clearPlayers = () => setSelectedPlayers([]);

  const handleCreate = () => {
    setTeams(splitTeams(selectedPlayers));
    setStep(STEPS.indexOf('teams'));
  };

  const reshuffle = () => {
    setTeams(splitTeams(selectedPlayers));
  };

  const teamMatch = useMemo(() => ({
    id: 'demo',
    type: matchType,
    team1: { players: teams.team1.map(p => ({ discord_user: { user_id: p.discord_id } })) },
    team2: { players: teams.team2.map(p => ({ discord_user: { user_id: p.discord_id } })) },
  }), [teams, matchType]);

  const handlePoolStarted = useCallback((config) => {
    setSelectedPool(config.pool || []);
    setVetoConfig({
      match_type: config.match_type || matchType,
      veto_mode: config.veto_mode || 'team_vote',
      knife_round: !!config.knife_round,
    });
    setResetKey(k => k + 1);
    setStep(STEPS.indexOf('veto'));
  }, [matchType]);

  const handleVetoComplete = useCallback(() => {
    // Brief delay so user sees the results, then jump to launching-mock
    setTimeout(() => setStep(STEPS.indexOf('launching')), 2000);
  }, []);

  const resetAll = () => {
    setStep(0);
    setSelectedPlayers([]);
    setMatchType('BO1');
    setTeams({ team1: [], team2: [] });
    setSelectedPool([]);
    setResetKey(0);
  };

  return (
    <div className="create-match-page">
      <div className="create-match-header">
        <h1 className="create-match-title">
          {t('createMatchDemo.title')}
          <span className="lobby-match-type">{matchType}</span>
        </h1>
        <button className="cancel-match-btn" onClick={resetAll}>
          {t('createMatchDemo.restart')}
        </button>
      </div>

      <p style={{ color: '#888', marginTop: '-0.8rem', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
        {t('createMatchDemo.subtitle')}
      </p>

      <div className="steps-indicator">
        {STEPS.map((s, i) => (
          <div key={s} className={`step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="step-num">{i + 1}</span>
            <span className="step-label">{t(STEP_LABEL_KEYS[s])}</span>
          </div>
        ))}
      </div>

      {currentStep === 'players' && (
        <div className="step-content">
          <div className="step-header">
            <h2>{t('createMatch.selectPlayers', { count: selectedPlayers.length })}</h2>
            <div className="match-type-select">
              <label>{t('createMatchDemo.matchTypeLabel')}</label>
              <select value={matchType} onChange={e => setMatchType(e.target.value)}>
                <option value="BO1">BO1</option>
                <option value="BO3">BO3</option>
                <option value="BO5">BO5</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={pickAll}
              style={{
                padding: '0.45rem 0.9rem', borderRadius: 6,
                border: '1px solid rgba(74,222,128,0.4)',
                background: 'rgba(74,222,128,0.1)', color: '#4ade80', cursor: 'pointer',
              }}
            >
              {t('createMatchDemo.selectAll10')}
            </button>
            <button
              onClick={clearPlayers}
              style={{
                padding: '0.45rem 0.9rem', borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent', color: '#888', cursor: 'pointer',
              }}
            >
              {t('createMatchDemo.clear')}
            </button>
          </div>

          <div className="player-grid">
            {FAKE_PLAYERS.map(player => {
              const isSelected = selectedPlayers.some(p => p.discord_id === player.discord_id);
              return (
                <div
                  key={player.discord_id}
                  className={`player-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => togglePlayer(player)}
                >
                  <div className="player-card-avatar-wrapper">
                    <span className="online-indicator" title={t('createMatchDemo.online')} />
                  </div>
                  <div className="player-card-info">
                    <span className="player-card-name">{player.username}</span>
                    <span className="player-card-elo">
                      <RankBadge rank={player.elo.rank} size="small" />
                      {player.elo.elo} ELO
                    </span>
                  </div>
                  <div className={`player-card-check ${isSelected ? 'checked' : ''}`}>
                    {isSelected ? '✓' : ''}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            className="next-btn"
            disabled={selectedPlayers.length < 2}
            onClick={handleCreate}
          >
            {t('createMatch.createWithCount', { count: selectedPlayers.length })}
          </button>
        </div>
      )}

      {currentStep === 'teams' && (
        <div className="step-content">
          <h2>{t('createMatchDemo.teamsCount', { a: teams.team1.length, b: teams.team2.length })}</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <TeamColumn label="Team 1" players={teams.team1} color="#4ade80" />
            <TeamColumn label="Team 2" players={teams.team2} color="#f59e0b" />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <button
              onClick={() => setStep(STEPS.indexOf('players'))}
              style={{
                padding: '0.5rem 1rem', borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent', color: '#888', cursor: 'pointer',
              }}
            >
              {t('profile.back')}
            </button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={reshuffle}
                style={{
                  padding: '0.5rem 1rem', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent', color: '#ddd', cursor: 'pointer',
                }}
              >
                {t('createMatchDemo.reshuffle')}
              </button>
              <button
                className="next-btn"
                onClick={() => setStep(STEPS.indexOf('map_pool'))}
              >
                {t('createMatch.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'map_pool' && (
        <div className="step-content">
          <MapPoolSelector
            match={teamMatch}
            gameMode="competitive"
            demoMode
            onVetoStarted={handlePoolStarted}
          />
          <div className="step-nav">
            <button
              className="back-btn"
              onClick={() => setStep(STEPS.indexOf('teams'))}
            >
              {t('profile.back')}
            </button>
          </div>
        </div>
      )}

      {currentStep === 'veto' && (
        <div className="step-content">
          <VetoSimulator
            matchType={vetoConfig.match_type}
            vetoMode={vetoConfig.veto_mode}
            pool={selectedPool}
            knifeRound={vetoConfig.knife_round}
            resetKey={resetKey}
            onComplete={handleVetoComplete}
            bo1BanFinish={!!features?.BO1_BAN_FINISH}
          />
        </div>
      )}

      {currentStep === 'launching' && (
        <div className="step-content lobby-launching" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
          <h2 style={{ marginTop: 0 }}>{t('createMatchDemo.matchCreated')}</h2>
          <p style={{ color: '#888', marginBottom: '2rem' }}>
            {t('createMatchDemo.productionNote')}
          </p>
          <button className="next-btn" onClick={resetAll}>{t('createMatchDemo.restart')}</button>
        </div>
      )}
    </div>
  );
}

function TeamColumn({ label, players, color }) {
  return (
    <div style={{
      border: `1px solid ${color}40`,
      background: `${color}10`,
      borderRadius: 8,
      padding: '0.8rem',
    }}>
      <h3 style={{ margin: '0 0 0.5rem 0', color, fontSize: '0.95rem' }}>{label}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {players.map(p => (
          <div
            key={p.discord_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem 0.6rem',
              background: 'rgba(0,0,0,0.25)',
              borderRadius: 4,
              fontSize: '0.85rem',
            }}
          >
            <span>{p.username}</span>
            <span style={{ color: '#888', fontSize: '0.75rem' }}>{p.elo.elo}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
